import type { SaleContext } from './types';

/**
 * resolve-sale-context.ts — stage 1 of the pipeline: ONE resolver rule for
 * every method and flow (closes V5 review C-03). Pure over explicit inputs;
 * the store adapter gathers them.
 *
 * Precedence: a locally owned sale (preallocated identity with a frozen
 * snapshot) wins — its snapshot is the money identity later legs must repeat
 * byte-identically. Otherwise an acknowledged canonical sale continues via
 * the API. Otherwise the attempt is fresh.
 */

export interface ResolveSaleContextInput {
  /** Canonical sale acknowledged by the backend, when one exists. */
  canonicalSaleId: string | null;
  /** Locally preallocated sale with its frozen snapshot, when one exists. */
  localSale: { saleId: string; snapshotHash: string } | null;
}

export function resolveSaleContext(input: ResolveSaleContextInput): SaleContext {
  if (input.localSale) {
    return {
      kind: 'existing-local',
      saleId: input.localSale.saleId,
      snapshotHash: input.localSale.snapshotHash,
    };
  }
  if (input.canonicalSaleId) {
    return { kind: 'existing-canonical', saleId: input.canonicalSaleId };
  }
  return { kind: 'fresh' };
}

// ---------------------------------------------------------------------------
// Snapshot hashing — a stable identity for the frozen sale snapshot so later
// legs can prove they repeat the same money identity. Not cryptographic; it
// only needs collision resistance adequate for change detection.
// ---------------------------------------------------------------------------

/** Deterministic JSON: object keys sorted, undefined stripped, cycles rejected. */
export function stableSnapshotString(value: unknown): string {
  const seen = new Set<object>();

  const normalize = (input: unknown): unknown => {
    if (input === null || typeof input !== 'object') {
      if (typeof input === 'number' && !Number.isFinite(input)) {
        throw new Error('Snapshot values must be finite numbers.');
      }
      return input;
    }
    if (seen.has(input)) throw new Error('Snapshot values must not contain cycles.');
    seen.add(input);
    try {
      if (Array.isArray(input)) return input.map((item) => normalize(item) ?? null);
      return Object.keys(input as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((accumulator, key) => {
          const entry = (input as Record<string, unknown>)[key];
          if (entry !== undefined) accumulator[key] = normalize(entry);
          return accumulator;
        }, {});
    } finally {
      seen.delete(input);
    }
  };

  return JSON.stringify(normalize(value));
}

/** cyrb53 — fast, well-distributed 53-bit hash rendered as fixed-width hex. */
/* eslint-disable no-bitwise -- the hash mixing rounds are inherently bitwise */
export function computeSnapshotHash(value: unknown): string {
  const text = stableSnapshotString(value);
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < text.length; index += 1) {
    const character = text.charCodeAt(index);
    h1 = Math.imul(h1 ^ character, 2654435761);
    h2 = Math.imul(h2 ^ character, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const combined = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return combined.toString(16).padStart(14, '0');
}
/* eslint-enable no-bitwise */
